import { Stack, Typography } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { useDebounceValue } from "../../../hooks/useDebounceValue";
import { useStateWithSearchParams } from "../../../hooks/useStateWithSearchParams";
import { CustomTextButton } from "../CustomTextButton";
import { DropdownWithSearchWrapper } from "./DropdownWithSearchWrapper";
import { DropdownSubItem } from "./DropdownSubItem";
import { QUERY_SEPARATOR } from "../../../constant";

type Props = {
  items: string[];
  query: string;
  placeholder: string;
  renderItem?: (item: string) => ReactNode;
  withSearch?: boolean;
  withReset?: boolean;
  isDisabled?: boolean;
  showCounter?: boolean;
  withCheckbox?: boolean;
};

type SearchParams = {
  [key: string]: string | undefined;
};

export const DropdownWithSearch = ({
  items,
  query,
  placeholder,
  renderItem,
  withSearch = true,
  withReset = true,
  isDisabled,
  showCounter = true,
  withCheckbox = true,
}: Props) => {
  const [search, setSearch] = useState<string>("");
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const searchValueDebounce = useDebounceValue(search);
  const { searchDict, setSearchValue } = useStateWithSearchParams<SearchParams>(
    { [query]: undefined },
  );

  const [selectedItemList, setSelectedItemList] = useState<string[]>([]);

  useEffect(() => {
    if (!searchDict[query] && selectedItemList.length === 0) return;

    setSelectedItemList(
      searchDict[query]?.length
        ? searchDict[query]?.split(QUERY_SEPARATOR) || []
        : [],
    );
  }, [searchDict[query]]);

  const handleChange = (item: string) => {
    let isInList = false;

    const newList = selectedItemList.filter((name) => {
      if (name === item) {
        isInList = true;
        return false;
      }

      return true;
    });

    !isInList && newList.push(item);
    setSelectedItemList(newList);
  };

  const closeHandler = () => {
    setIsOpen(false);
    setSelectedItemList(searchDict[query]?.split(QUERY_SEPARATOR) || []);
    setSearch("");
  };

  const cancelHandler = () => {
    setIsOpen(false);
    setSearch("");
    setSelectedItemList(
      searchDict[query]?.length
        ? searchDict[query]?.split(QUERY_SEPARATOR) || []
        : [],
    );
  };

  const submitHandler = () => {
    setIsOpen(false);
    setSearch("");
    setSearchValue(query, selectedItemList.join(QUERY_SEPARATOR));
  };

  const resetSelected = () => {
    setSearchValue(query, undefined);
  };

  const hasSelected = !!(searchDict[query]?.split(QUERY_SEPARATOR)[0] || [])
    .length;

  return (
    <Stack direction="row" gap="8px">
      <DropdownWithSearchWrapper
        isDisabled={isDisabled}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        placeholder={placeholder}
        closeHandler={closeHandler}
        cancelHandler={cancelHandler}
        submitHandler={submitHandler}
        selectedCount={searchDict[query]?.split(QUERY_SEPARATOR).length || 0}
        search={search}
        setSearch={setSearch}
        showCounter={showCounter}
        withSearch={withSearch}
      >
        {items.length > 0 ? (
          items
            .filter((el) =>
              el.toLowerCase().includes(searchValueDebounce.toLowerCase()),
            )
            .map((name) => (
              <DropdownSubItem
                key={name}
                title={name}
                checked={selectedItemList.includes(name)}
                onClick={() => handleChange(name)}
                renderItem={renderItem}
                withCheckbox={withCheckbox}
              />
            ))
        ) : (
          <Typography
            variant="body1"
            color="#4C545B"
            textAlign="center"
            paddingY="8px"
          >
            No {placeholder.toLowerCase()} found yet
          </Typography>
        )}
      </DropdownWithSearchWrapper>

      {hasSelected && withReset && (
        <CustomTextButton onClick={resetSelected}>Reset</CustomTextButton>
      )}
    </Stack>
  );
};
